import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Helmet from "react-helmet";
import { useDispatch } from "react-redux";
import Footer from "../nav_bar/Footer";
import ContentPreFooter from "../../app/components/ContentPreFooter";
import HomeNavBar, { INSTITUTE_URL } from "./HomeNavBar";
import ExpertCulinaryCategories from "./ExpertCulinaryCategories";
import TalentDiscoveryHub from "./TalentDiscoveryHub";
import { topjob, topChefs } from "../../store/slices/config";
import chefFallback from "../../Assets/Images/chef.png";
import jobSnapshotFallback from "../../Assets/Images/chefs-hat-22.webp";
import { getMeaningfulAbout } from "../../utils/helpers/chefMarketplaceUtils";
import styles from "./HomeLanding.module.css";

const HomeChatBot = lazy(() => import("../../utils/helpers/ChatBot2/HomeChatBot"));
const TestimonialsSection = lazy(() => import("./TestimonialsSection"));
const TrustedByBrands = lazy(() => import("./TrustedByBrands"));

const SNAPSHOT_LIMIT = 4;

const METRICS = [
  {
    value: "100% Dedicated",
    text: "Focused purely on the culinary sector with zero dilution.",
  },
  {
    value: "Authenticated Network",
    text: "Transparent profile validation tracks with active portfolios.",
  },
  {
    value: "₹0 Commission",
    text: "Direct sourcing model. Keep 100% of your operational budget.",
  },
];

function parseSalaryValue(salary) {
  if (salary == null || salary === "") return null;
  if (typeof salary === "number" && !Number.isNaN(salary)) return salary;
  const parsed = parseInt(String(salary).replace(/\D/g, ""), 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function formatSalary(salary) {
  const value = parseSalaryValue(salary);
  if (value == null) return null;
  if (value >= 1000) return `₹${Math.round(value / 1000)}k/mo`;
  return `₹${value}/mo`;
}

function formatExperience(years) {
  if (years == null || years === "") return null;
  const value = Number(years);
  if (Number.isNaN(value)) return null;
  return `${value} yr${value === 1 ? "" : "s"} exp`;
}

function formatPostedDate(dateString) {
  if (!dateString) return null;
  const diffMs = Date.now() - new Date(dateString).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Posted just now";
  if (minutes < 60) return `Posted ${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Posted ${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Posted ${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  return `Posted ${months} month${months === 1 ? "" : "s"} ago`;
}

function normalizeTags(items, max = 2) {
  if (!Array.isArray(items)) return [];
  return items.filter(Boolean).slice(0, max);
}

function normalizeChefCuisines(chef) {
  const cuisines = normalizeTags(chef?.chefCuisines, 2);
  if (cuisines.length) return cuisines;
  return normalizeTags(chef?.chefSkills, 2);
}

function truncateText(value, maxLength = 72) {
  if (!value) return "";
  const text = String(value).trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}…`;
}

function DeferredSection({ children }) {
  const containerRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (shouldRender) return undefined;
    if (!("IntersectionObserver" in window)) {
      setShouldRender(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldRender(true);
        observer.disconnect();
      },
      { rootMargin: "600px 0px" }
    );

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [shouldRender]);

  return (
    <div ref={containerRef}>
      {shouldRender ? <Suspense fallback={null}>{children}</Suspense> : null}
    </div>
  );
}

function IdleRender({ children }) {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const show = () => setShouldRender(true);
    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(show, { timeout: 2500 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timerId = window.setTimeout(show, 1500);
    return () => window.clearTimeout(timerId);
  }, []);

  return shouldRender ? <Suspense fallback={null}>{children}</Suspense> : null;
}

function SnapshotAvatar({ src, alt, fallback }) {
  const [failed, setFailed] = useState(false);
  const imageSrc = !src || failed ? fallback : src;

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={styles.snapshotAvatar}
      loading="lazy"
      width="48"
      height="48"
      onError={() => setFailed(true)}
    />
  );
}

function JobSnapshotCard({ job }) {
  const metaParts = [
    formatExperience(job?.experience),
    formatSalary(job?.salary),
    job?.location,
  ].filter(Boolean);
  const cuisines = normalizeTags(job?.cuisines, 2);
  const posted = formatPostedDate(job?.createdAt);

  return (
    <Link to="/chef-jobs" className={styles.snapshotItem}>
      <SnapshotAvatar
        src={job?.dp}
        alt={job?.designation || "Employer"}
        fallback={jobSnapshotFallback}
      />
      <div className={styles.snapshotBody}>
        <div className={styles.snapshotHeader}>
          <h3 className={styles.snapshotItemTitle}>
            {truncateText(job?.designation || "Chef Position", 15)}
          </h3>
        </div>
        {metaParts.length > 0 && (
          <p className={styles.snapshotMeta}>{metaParts.join(" · ")}</p>
        )}
        {cuisines.length > 0 ? (
          <div className={styles.snapshotTags}>
            {cuisines.map((cuisine) => (
              <span key={cuisine} className={styles.tagChip}>
                {cuisine}
              </span>
            ))}
          </div>
        ) : (
          <div className={styles.snapshotTagsSpacer} aria-hidden />
        )}
        <p className={styles.snapshotDate}>{posted || "\u00A0"}</p>
      </div>
    </Link>
  );
}

function ChefSnapshotCard({ chef }) {
  const salaryLabel = formatSalary(chef?.expectedSalary);
  const experienceLabel = formatExperience(chef?.chefExperience);
  const metaParts = [
    salaryLabel,
    chef?.currentCityName,
    experienceLabel,
  ].filter(Boolean);
  const cuisines = normalizeChefCuisines(chef);
  const aboutText = getMeaningfulAbout(chef?.about, 72);

  return (
    <Link to="/cooks/chef" className={styles.snapshotItem}>
      <SnapshotAvatar
        src={chef?.dp}
        alt={chef?.fullName || "Chef profile"}
        fallback={chefFallback}
      />
      <div className={styles.snapshotBody}>
        <div className={styles.snapshotHeader}>
          <h3 className={styles.snapshotItemTitle}>
            {chef?.fullName || "Verified Chef"}
          </h3>
          <span className={styles.snapshotAction}>View Profile →</span>
        </div>
        {metaParts.length > 0 && (
          <p className={styles.snapshotMeta}>{metaParts.join(" · ")}</p>
        )}
        {cuisines.length > 0 ? (
          <div className={styles.snapshotTags}>
            {cuisines.map((cuisine) => (
              <span key={cuisine} className={styles.tagChip}>
                {cuisine}
              </span>
            ))}
          </div>
        ) : (
          <div className={styles.snapshotTagsSpacer} aria-hidden />
        )}
        {aboutText ? (
          <p className={styles.snapshotAbout} title={chef?.about || undefined}>
            {aboutText}
          </p>
        ) : (
          <p className={styles.snapshotAbout} aria-hidden>
            {"\u00A0"}
          </p>
        )}
      </div>
    </Link>
  );
}

export default function Home() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [chefData, setChefData] = useState([]);
  const [jobData, setJobData] = useState([]);

  useEffect(() => {
    const userString = localStorage.getItem("user");
    if (!userString) return;

    try {
      const user = JSON.parse(userString);
      if (user?.data?.role === "cook") {
        navigate("/cook/dashboard");
      } else if (user?.data?.role === "employer") {
        navigate("/employer/dashboard");
      } else if (user?.data?.role === "client") {
        navigate("/client/dashboard");
      }
    } catch {
      // ignore invalid session payload on public homepage
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    const loadSnapshots = () => {
      if (cancelled) return;

      dispatch(topChefs())
        .unwrap()
        .then((data) => {
          if (!cancelled) setChefData(data.topchef.data || []);
        })
        .catch(() => {});

      dispatch(topjob())
        .unwrap()
        .then((data) => {
          if (!cancelled) setJobData(data.topjobs.data || []);
        })
        .catch(() => {});
    };

    let cleanup;
    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(loadSnapshots, { timeout: 2000 });
      cleanup = () => window.cancelIdleCallback(idleId);
    } else {
      const timerId = window.setTimeout(loadSnapshots, 1200);
      cleanup = () => window.clearTimeout(timerId);
    }

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [dispatch]);

  const visibleJobs = jobData.slice(0, SNAPSHOT_LIMIT);
  const visibleChefs = chefData.slice(0, SNAPSHOT_LIMIT);

  return (
    <>
      <Helmet>
        <title>
          CookandChef | Hire Chefs & Cooks Online | Find Cooking Jobs in India
        </title>
        <meta
          name="description"
          content="CookandChef is India's largest dedicated culinary hiring marketplace. Connect restaurants, hotels, and households with verified chefs and cooks."
        />
        <meta property="og:url" content="https://cookandchef.in/" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Cook and Chef | Hire Chefs & Cooks Online"
        />
        <meta
          property="og:description"
          content="CookandChef is India's largest dedicated culinary hiring marketplace. Connect restaurants, hotels, and households with verified chefs and cooks."
        />
        <meta
          property="og:image"
          content="https://cookandchef.in/static/media/nav_logo.1a9553aeb643762dbf26.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="cookandchef.in" />
        <meta property="twitter:url" content="https://cookandchef.in/" />
        <meta
          name="twitter:title"
          content="Cook and Chef | Hire Chefs & Cooks Online"
        />
        <meta
          name="twitter:description"
          content="CookandChef is India's largest dedicated culinary hiring marketplace."
        />
        <link rel="canonical" href="https://cookandchef.in/" />
      </Helmet>

      <div className={styles.landing}>
        <HomeNavBar />

        <section className={styles.hero}>
          <h1 className={styles.heroTitle}>
            India&apos;s Largest Dedicated Culinary Hiring Marketplace
          </h1>
          <p className={styles.heroTagline}>
            Connect instantly with chefs, restaurant kitchen teams, and expert
            home cooks. Skip unreliable offline agencies.
          </p>

          <div className={styles.gatewayGrid}>
            <article
              className={`${styles.gatewayCard} ${styles.gatewayCardDark}`}
            >
              <h2 className={styles.gatewayTitle}>For Restaurants &amp; Hotels</h2>
              <ul className={styles.gatewayList}>
                <li>Executive / Sous Chefs</li>
                <li>Commi 1 / 2 / 3</li>
                <li>Cloud Kitchen Teams</li>
              </ul>
              <Link
                to="/employer-registration?type=business"
                className={styles.btnPrimary}
              >
                Hire a Chef
              </Link>
            </article>

            <article
              className={`${styles.gatewayCard} ${styles.gatewayCardLight}`}
            >
              <h2 className={styles.gatewayTitle}>For Homes &amp; Events</h2>
              <ul className={styles.gatewayList}>
                <li>Daily House Cooks</li>
                <li>Special Party Cooks</li>
                <li>Full Catering Teams</li>
              </ul>
              <Link to="/employer-registration?type=home" className={styles.btnGhost}>
                Find a Cook for Home
              </Link>
            </article>
          </div>
        </section>

        <TalentDiscoveryHub />

        <section className={styles.jobBannersSection} aria-label="Job opportunities">
          <div className={styles.jobBannersGrid}>
            <article className={`${styles.jobBanner} ${styles.jobBannerBlue}`}>
              <p className={styles.jobBannerEyebrow}>Professional Chef Jobs (HoReCa Focus)</p>
              <h2 className={styles.jobBannerTitle}>
                Find Professional Chef Jobs Near You
              </h2>
              <p className={styles.jobBannerSubtitle}>
                Join top restaurants, hotels, cafes, and cloud kitchens. Apply
                directly to active culinary vacancies with zero registration
                fees.
              </p>
              <Link to="/chef-jobs" className={styles.jobBannerBtnBlue}>
                Explore Chef Jobs →
              </Link>
            </article>

            <article className={`${styles.jobBanner} ${styles.jobBannerOrange}`}>
              <p className={styles.jobBannerEyebrow}>
                Domestic House Cook Jobs (Household Focus)
              </p>
              <h2 className={styles.jobBannerTitle}>
                Cook &amp; Catering Jobs in Local Households
              </h2>
              <p className={styles.jobBannerSubtitle}>
                Get verified cooking jobs for families, private parties, and
                domestic events. Earn a stable monthly income near your
                residential area.
              </p>
              <Link to="/housecook-jobs" className={styles.jobBannerBtnOrange}>
                Explore House Cook Jobs →
              </Link>
            </article>
          </div>
        </section>

        <section className={styles.snapshotSection}>
          <div className={styles.snapshotGrid}>
            <article className={styles.snapshotCard}>
              <div className={styles.snapshotSectionHead}>
                <h2 className={styles.snapshotTitle}>Active Jobs</h2>
                <Link to="/chef-jobs" className={styles.snapshotViewAll}>
                  View All →
                </Link>
              </div>
              <div className={styles.snapshotList}>
                {visibleJobs.length > 0 ? (
                  visibleJobs.map((job, index) => (
                    <JobSnapshotCard key={job?._id || index} job={job} />
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    Fresh culinary gigs are loading. Explore the full job board
                    for live openings.
                  </div>
                )}
              </div>
            </article>

            <article className={styles.snapshotCard}>
              <div className={styles.snapshotSectionHead}>
                <h2 className={styles.snapshotTitle}>Browse Chefs</h2>
                <Link to="/cooks/chef" className={styles.snapshotViewAll}>
                  View All →
                </Link>
              </div>
              <div className={styles.snapshotList}>
                {visibleChefs.length > 0 ? (
                  visibleChefs.map((chef, index) => (
                    <ChefSnapshotCard key={chef?._id || index} chef={chef} />
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    Verified chef profiles are loading. Browse all available
                    talent on the network.
                  </div>
                )}
              </div>
            </article>
          </div>
        </section>

        <ExpertCulinaryCategories />

        <section className={styles.instituteRow}>
          <div className={styles.instituteBox}>
            <div>
              <h2 className={styles.instituteTitle}>
                Sourcing the Next Generation of Culinary Excellence.
              </h2>
              <p className={styles.instituteText}>
                Through the CookandChef Training Institute, we actively educate,
                evaluate, and upskill raw kitchen talents to maintain elite
                workforce standards for India&apos;s top hospitality chains.
              </p>
            </div>
            <a
              href={INSTITUTE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.instituteLink}
            >
              Visit Our Institute Academy →
            </a>
          </div>
        </section>

        <DeferredSection>
          <TrustedByBrands />
        </DeferredSection>

        <DeferredSection>
          <TestimonialsSection />
        </DeferredSection>

        <section className={styles.academySection} aria-label="Culinary Academy">
          <div className={styles.academyCard}>
            <p className={styles.academyText}>
              Connect culinary institutes with top hospitality brands. Register
              students, manage placements, and grow your institute&apos;s industry
              network.
            </p>
            <a
              href={INSTITUTE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.academyBtn}
            >
              Browse Culinary Academy
            </a>
          </div>
        </section>

        <section className={styles.metricsSection}>
          <div className={styles.metricsBanner}>
            {METRICS.map((metric) => (
              <article key={metric.value}>
                <p className={styles.metricValue}>{metric.value}</p>
                <p className={styles.metricText}>{metric.text}</p>
              </article>
            ))}
          </div>
        </section>
      </div>

      <div className={styles.chatbotWrap}>
        <IdleRender>
          <HomeChatBot />
        </IdleRender>
      </div>

      <ContentPreFooter />
      <Footer />
    </>
  );
}
